import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { type PageLayoutStore } from 'shared/layout'
import { toastStore } from 'shared/ui'
import { applyCustomSort, mergeReorderedSubset } from 'shared/ui/DragAndDrop'
import { uiStore } from 'shared/store/uiStore'
import { RequestCancelAxios } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { type Segment } from 'entities/Segment/model/Segment'
import { IntegrationKey, IntegrationsApi } from 'entities/Integrations'
import { type IResponseFilterSegment } from 'entities/Segment'
import { UsersApi } from 'entities/Users'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type SegmentModalStore } from 'pages/contacts/ui/ContactsMenu/ui/SegmentModal'
import { ContactsPath } from 'pages/contacts/route/contactsPath'
import { urlSegmentId } from 'pages/contacts/constants/constants'
import { MenuSegmentsStore } from './menuSegmentsStore'

const getFavoriteSegmentId = (segment: Segment) => String(segment.id)

type IContactsMenuStoreConfig = {
  setActiveSegment: (segment: Segment) => void
  changeActiveSegment: (segment: Segment) => void
  resetActive: () => void
  onActionAllContacts: () => void
  onActionChangeSegment: () => void
  onActionFilterSegmentBoxSetHasChanges: (value: boolean) => void
  onActionFilterOpen: (value: boolean) => void
  onChangeTitle?: (value: string) => void
}

export class ContactsMenuStore {
  initPromise
  setFinishInit: ((value?: unknown) => void) | null = null
  private _search = ''
  private _segmentsSearch = ''
  private _hubspotListsSearch = ''
  private _favoritesSearch = ''
  private _activeSegment: Segment | null = null
  private _config: IContactsMenuStoreConfig | null = null
  private _noEmptyFilters: IResponseFilterSegment[] = []
  private _segmentBoxHasChanges = false
  private _menuSegmentsStore: MenuSegmentsStore
  private _isSyncingHubspotLists = false
  private _hubspotListLastSyncedAt = new Map<number, number>()
  private _HUBSPOT_SYNC_COOLDOWN_MS = 60_000
  private _favoritesOrder: string[] = []
  private _favoritesOrderLoaded = false
  private _cancelRequestLoadFavoritesOrder = new RequestCancelAxios()

  constructor(
    private _segmentModalStore: SegmentModalStore,
    private _pageLayoutStore: PageLayoutStore,
    private _entities: IEntitiesStore
  ) {
    this._menuSegmentsStore = new MenuSegmentsStore(this._entities)
    this.initPromise = new Promise((resolve) => {
      this.setFinishInit = resolve
    })
    this.loadFavoritesOrder()

    makeAutoObservable(this)
  }

  get title() {
    if (!this.activeSegment || this.activeSegment.id === -1) return 'All contacts'

    return this.activeSegment.name
  }

  get search() {
    return this._search
  }

  get segmentBoxHasChanges() {
    return this._segmentBoxHasChanges
  }

  get segmentsSearch() {
    return this._segmentsSearch
  }

  get hubspotListsSearch() {
    return this._hubspotListsSearch
  }

  get favoritesSearch() {
    return this._favoritesSearch
  }

  get segmentsAll() {
    return this._menuSegmentsStore.items.filter((filter) =>
      filter.name
        .toLocaleLowerCase()
        .includes(this._segmentsSearch.toLocaleLowerCase() || this._search.toLocaleLowerCase())
    )
  }

  get segments() {
    return this._menuSegmentsStore.items
      .filter((filter) => !filter.isFavorite)
      .filter((filter) => !filter.hasHubspotFilter)
      .filter((filter) =>
        filter.name
          .toLocaleLowerCase()
          .includes(this._segmentsSearch.toLocaleLowerCase() || this._search.toLocaleLowerCase())
      )
  }

  get hubspotLists() {
    return this.hasHubspotConnected()
      ? this._menuSegmentsStore.items
          .filter((filter) => !filter.isFavorite)
          .filter((filter) => filter.hasHubspotFilter)
          .filter((filter) =>
            filter.name
              .toLocaleLowerCase()
              .includes(
                this._hubspotListsSearch.toLocaleLowerCase() || this._search.toLocaleLowerCase()
              )
          )
      : []
  }

  private get _favoritesBeforeSearch() {
    return this._menuSegmentsStore.items
      .filter((filter) => {
        if (!this.hasHubspotConnected()) {
          return !filter.hasHubspotFilter
        }

        return true
      })
      .filter((filter) => filter.isFavorite)
  }

  get favorites() {
    const filtered = this._favoritesBeforeSearch.filter((filter) =>
      filter.name
        .toLocaleLowerCase()
        .includes(this._favoritesSearch.toLocaleLowerCase() || this._search.toLocaleLowerCase())
    )

    return applyCustomSort(filtered, this._favoritesOrder, getFavoriteSegmentId, {
      newItemsAt: 'bottom',
    })
  }

  get favoritesOrderLoaded() {
    return this._favoritesOrderLoaded
  }

  get activeSegment() {
    return this._activeSegment
  }

  get isNewSegment() {
    return this._activeSegment?.id === -1
  }

  get activeSegmentId() {
    return this._activeSegment?.id ?? null
  }

  setSegmentBoxHasChanges = (value: boolean) => {
    this._segmentBoxHasChanges = value
  }

  setNoEmptyFilters = (value: IResponseFilterSegment[]) => {
    this._noEmptyFilters = value
  }

  setConfig = (config: IContactsMenuStoreConfig) => {
    this._config = config
  }

  setActiveSegment = (segment: Segment | null) => {
    this._activeSegment = segment
    if (segment) this._config?.setActiveSegment(segment)
    if (segment?.hasHubspotFilter) {
      const listIds = this._getSegmentHubspotListIds(segment)
      this.syncHubspotLists(listIds)
    }
  }

  changeActiveSegment = (segment: Segment) => {
    this.setActiveSegment(segment)
    this._config?.setActiveSegment(segment)
  }

  loadFavoritesOrder = async () => {
    try {
      this._cancelRequestLoadFavoritesOrder.init()

      const { data } = await UsersApi.getUsersUiSettings('sort-segment-favorite', {
        cancelToken: this._cancelRequestLoadFavoritesOrder.token,
      })

      runInAction(() => {
        this._favoritesOrder = data.data?.items ?? []
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._favoritesOrderLoaded = true
      })
    }
  }

  reorderFavorites = async (orderedIds: string[]) => {
    try {
      this._cancelRequestLoadFavoritesOrder.reset()

      const fullCurrentOrder = applyCustomSort(
        this._favoritesBeforeSearch,
        this._favoritesOrder,
        getFavoriteSegmentId,
        { newItemsAt: 'bottom' }
      ).map(getFavoriteSegmentId)

      const merged = mergeReorderedSubset(fullCurrentOrder, orderedIds)

      const isSameOrder =
        merged.length === this._favoritesOrder.length &&
        merged.every((id, index) => id === this._favoritesOrder[index])

      if (isSameOrder) return

      runInAction(() => {
        this._favoritesOrder = merged
      })

      await UsersApi.updateUsersUiSettings({
        source_type: 'sort-segment-favorite',
        items: merged,
      })
    } catch (e) {
      logger.error(e)
    }
  }

  dispose = () => {
    this._cancelRequestLoadFavoritesOrder.reset()
    this._menuSegmentsStore.dispose()
  }

  resetActive = () => {
    this._config?.resetActive()
  }

  private _navToAllContacts = () => {
    this.resetActive()
    this._config?.onActionAllContacts()

    uiStore.changeRoute({
      path: ContactsPath.root,
    })
  }

  navToAllContactsAsk = () => {
    if (!this.isNewSegment && this._segmentBoxHasChanges) {
      this._segmentModalStore.openAskIfSaveModal({
        onToggle: this._navToAllContacts,
        noSetActive: true,
        filtersList: this._noEmptyFilters,
      })
    } else {
      this._navToAllContacts()
    }
  }

  private _onActionChangeSegment = (segment: Segment) => {
    uiStore.changeRoute({
      path: `${ContactsPath.root}?${urlSegmentId}=${segment.id}`,
    })

    this.changeActiveSegment(segment)
    this._config?.onActionChangeSegment()
  }

  onActionChangeSegmentAsk = (segment: Segment) => {
    if (this.activeSegment?.id === segment.id) return

    if (!this.isNewSegment && this._segmentBoxHasChanges) {
      this._segmentModalStore.openAskIfSaveModal({
        onToggle: () => {
          this._onActionChangeSegment(segment)
        },
        noSetActive: true,
        filtersList: this._noEmptyFilters,
      })
    } else {
      this._onActionChangeSegment(segment)
    }
  }

  onActionFilterSegmentBoxSetHasChanges = (value: boolean) => {
    this._config?.onActionFilterSegmentBoxSetHasChanges(value)
  }

  onActionFilterOpen = (value: boolean) => {
    this._config?.onActionFilterOpen(value)
  }

  setSearch = (value: string) => {
    this._search = value
  }

  setSegmentsSearch = (value: string) => {
    this._segmentsSearch = value
  }
  setHubspotListsSearch = (value: string) => {
    this._hubspotListsSearch = value
  }
  setFavoritesSearch = (value: string) => {
    this._favoritesSearch = value
  }

  hasHubspotConnected = () => {
    const integration = this._entities.integrationsStore.getIntegration(IntegrationKey.hubspot)

    return !!integration?.isConnected
  }

  private _getSegmentHubspotListIds = (segment: Segment): number[] =>
    segment.filtersList
      .flatMap((group) => group.integration_lists ?? [])
      .flatMap((item) => {
        if (Array.isArray(item.list_id)) return item.list_id.map(Number)
        return item.list_id ? [Number(item.list_id)] : []
      })
      .filter((id) => !isNaN(id) && id !== 0)

  syncHubspotLists = async (selectedListIds: number[]) => {
    if (this._isSyncingHubspotLists || !this.hasHubspotConnected() || !selectedListIds.length)
      return
    this._isSyncingHubspotLists = true

    try {
      const { data: lists } = await IntegrationsApi.getHubspotLists()
      const dateNow = Date.now()
      const listsToSync = lists
        .filter((list) => selectedListIds.includes(list.list_id))
        .filter((list) => {
          const lastSynced = this._hubspotListLastSyncedAt.get(list.id)
          return !lastSynced || dateNow - lastSynced >= this._HUBSPOT_SYNC_COOLDOWN_MS
        })

      const results = await Promise.allSettled(
        listsToSync.map(async (list) => {
          await IntegrationsApi.resyncHubspotLists(list.id)
          runInAction(() => {
            this._hubspotListLastSyncedAt.set(list.id, Date.now())
          })
        })
      )
      results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .forEach((result) => logger.error(result.reason))
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isSyncingHubspotLists = false
      })
    }
  }

  editFavorite = async ({ afterEdit, segment }: { afterEdit?: () => void; segment: Segment }) => {
    if (!segment) return

    try {
      if (segment.isFavorite) {
        await this._entities.segmentsStore.makeUnfavoriteItem(segment.id)
      } else {
        await this._entities.segmentsStore.makeFavoriteItem(segment.id)
      }

      afterEdit?.()
    } catch (e) {
      if (e instanceof AxiosError) {
        const errorMessage = e.errors.getError('name') || e.message

        toastStore.add({
          title: errorMessage,
          type: 'error',
        })
      }
    }
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get segmentModalStore() {
    return this._segmentModalStore
  }
}
